import { useQuery } from '@tanstack/react-query';
import { ApprovalType, GlobalApprovalMode } from '@mg/shared';
import { api } from './api';
import { useTenant } from '../auth/TenantContext';

/** GlobalApprovalMode → the ApprovalType it forces, or null to defer to the workspace's own. */
export function globalToApprovalType(mode: GlobalApprovalMode): ApprovalType | null {
  switch (mode) {
    case GlobalApprovalMode.CAMPAIGN:
      return ApprovalType.CAMPAIGN;
    case GlobalApprovalMode.POST:
      return ApprovalType.POST;
    case GlobalApprovalMode.NONE:
      return ApprovalType.NONE;
    case GlobalApprovalMode.PER_WORKSPACE:
      return null;
  }
}

/**
 * The EFFECTIVE approval mode for the active workspace: the install-wide global mode
 * (PlatformSettings.globalApprovalMode) wins unless it defers to the workspace (PER_WORKSPACE),
 * in which case the workspace's own approvalType applies. Mirrors the server's
 * ApprovalPolicyService.getApprovalType so client gating never diverges from what the API enforces
 * (which is why the composer must use THIS, not the raw workspace type — the global mode decides
 * campaign-level vs per-post approval and whether one-off posts are allowed).
 */
export function useEffectiveApprovalType(): ApprovalType {
  const { activeTenant } = useTenant();
  const workspaceType = activeTenant?.approvalType ?? ApprovalType.CAMPAIGN;
  const platformQ = useQuery({
    queryKey: ['platform-settings'],
    queryFn: ({ signal }) => api.getPlatformSettings(signal),
  });
  const globalMode = platformQ.data?.globalApprovalMode;
  return (globalMode ? globalToApprovalType(globalMode) : null) ?? workspaceType;
}
